import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Planet, PlayerPremium, PlayerShipUpgrades, ShipId, TickCheckpointEvent } from '@kaeliux/shared';
import { normalizeEvent, parsePlayerPremium } from '@kaeliux/shared';
import { tickPlanet } from './planet.service';
import { normalizePlanet } from './planetNormalization';
import { getSelectedUniverseId } from '../store/universeStore';
import { authedPost } from './http';

/**
 * Accepted (but ignored) by every real gameplay call, mirroring
 * `PlanetActionOptions` in `planet.service.ts` — mock mode consumes these
 * fields since it has no server-side universe doc to read from; the real
 * backend fetches its own. Kept so callers can pass one options object
 * regardless of which service implementation is aliased in.
 */
export interface ShipUpgradeActionOptions {
    resourceMultiplier?: number;
    sunAges?: Record<string, number[]>;
    speedMultiplier?: number;
}

/** Player-wide state returned alongside the planet by the ship-upgrade endpoints. */
export interface ShipUpgradePlayerState {
    shipUpgrades: PlayerShipUpgrades;
    tickCheckpoints: TickCheckpointEvent[];
    /**
     * The planet as of the tick `getShipUpgradeState` performs to resolve due
     * upgrades. Set only by that getter — see the same field in
     * `science.service.ts`'s `SciencePlayerState`.
     */
    planet?: Planet;
}

export interface QueueShipUpgradeResult {
    planet: Planet;
    player: ShipUpgradePlayerState;
    /** Only present on `rushShipUpgrade`'s response — the other callers of this shape don't debit Dark Matter. */
    premium?: PlayerPremium;
}

/**
 * The functions respond with `{ ...planetResponse(state), player:
 * playerResponse(state) }` — planet fields at the top level (same shape
 * `tickPlanet`/`upgradeBuilding` return) plus a nested `player` object,
 * mirroring `science.service.ts`'s `normalizeResult`. `rushShipUpgrade`
 * additionally includes a top-level `premium`.
 */
function normalizeResult(raw: unknown, planetId: string): QueueShipUpgradeResult {
    const data = raw as Record<string, unknown>;
    const { player, premium, ...planetFields } = data;
    const playerData = (player ?? {}) as Record<string, unknown>;
    return {
        planet: normalizePlanet(planetFields, planetId),
        player: {
            shipUpgrades: (playerData.shipUpgrades ?? {}) as PlayerShipUpgrades,
            tickCheckpoints: ((playerData.tickCheckpoints ?? []) as unknown[]).map(normalizeEvent),
        },
        ...(premium !== undefined ? { premium: parsePlayerPremium(premium) } : {}),
    };
}

export async function queueShipUpgrade(
    planetId: string,
    shipId: ShipId,
    _options?: ShipUpgradeActionOptions,
): Promise<QueueShipUpgradeResult> {
    const data = await authedPost<unknown>('/queueShipUpgrade', { planetId, shipId });
    return normalizeResult(data, planetId);
}

export async function cancelShipUpgrade(
    planetId: string,
    eventId: number,
    _options?: ShipUpgradeActionOptions,
): Promise<QueueShipUpgradeResult> {
    const data = await authedPost<unknown>('/cancelShipUpgrade', { planetId, eventId });
    return normalizeResult(data, planetId);
}

/** Pay Dark Matter to instantly complete a pending shipUpgrade event. */
export async function rushShipUpgrade(
    planetId: string,
    eventId: number,
    _options?: ShipUpgradeActionOptions,
): Promise<QueueShipUpgradeResult> {
    const data = await authedPost<unknown>('/rushShipUpgrade', { planetId, eventId });
    return normalizeResult(data, planetId);
}

/**
 * Read current player state (ship-upgrade levels + pending queue) from the
 * server-owned `universes/{u}/players/{uid}` doc — the only place completed
 * upgrade levels and the queue actually live (writes are Admin-SDK-only, via
 * the Cloud Functions above; `firestore.rules` allows this read).
 *
 * There is no dedicated "resolve the profile's due ship upgrades" endpoint,
 * so this first calls `tickPlanet(planetId)` — same reasoning as
 * `science.service.ts`'s `getPlayerState`: the existing replay-only endpoint
 * already threads through `commitAdvance`, which resolves ANY due
 * `shipUpgrade` event on the profile (not just the given planet's own
 * events) and bumps the level in the same transaction.
 */
export async function getShipUpgradeState(uid: string, planetId: string): Promise<ShipUpgradePlayerState> {
    // Same contract as `getPlayerState`: this ticks to resolve due upgrades,
    // so hand the resulting planet back instead of dropping it.
    const planet = await tickPlanet(planetId);

    const ref = doc(db, 'universes', getSelectedUniverseId(), 'players', uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
        return { shipUpgrades: {}, tickCheckpoints: [], planet };
    }
    const data = snap.data() as Record<string, unknown>;
    return {
        shipUpgrades: (data.shipUpgrades ?? {}) as PlayerShipUpgrades,
        tickCheckpoints: ((data.tickCheckpoints ?? []) as unknown[]).map(normalizeEvent),
        planet,
    };
}
